import { useEffect, useState } from 'react';
import { settings } from '../content';

export type GithubStats = {
  publicRepos: number | null;
  contributionsLastYear: number | null;
};

type CacheEntry = { fetchedAt: number; stats: GithubStats };

const CACHE_KEY = 'ideaprojects.github-stats.v1';
const EMPTY: GithubStats = {
  publicRepos: null,
  contributionsLastYear: null,
};

const readCache = (): GithubStats | null => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    const ageMinutes = (Date.now() - entry.fetchedAt) / 60000;
    return ageMinutes < settings.statsCacheMinutes ? entry.stats : null;
  } catch {
    return null;
  }
};

const writeCache = (stats: GithubStats) => {
  try {
    const entry: CacheEntry = { fetchedAt: Date.now(), stats };
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    /* private mode / quota — the page works fine without a cache */
  }
};

const getJson = async (url: string, signal: AbortSignal) => {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`${response.status} ${url}`);
  return response.json();
};

/**
 * Every stat resolves on its own, so a rate-limited or offline endpoint
 * only blanks out its own row instead of emptying the whole block.
 */
export const useGithubStats = (): { stats: GithubStats; loading: boolean } => {
  const [stats, setStats] = useState<GithubStats>(() => readCache() ?? EMPTY);
  const [loading, setLoading] = useState(() => readCache() === null);

  useEffect(() => {
    if (readCache()) return;

    const controller = new AbortController();
    const { signal } = controller;
    const user = settings.githubUser;

    const load = async () => {
      const [profile, contributions] = await Promise.allSettled([
        getJson(`https://api.github.com/users/${user}`, signal),
        getJson(`https://github-contributions-api.jogruber.de/v4/${user}?y=last`, signal),
      ]);

      if (signal.aborted) return;

      const next: GithubStats = {
        publicRepos:
          profile.status === 'fulfilled' ? (profile.value.public_repos ?? null) : null,
        contributionsLastYear:
          contributions.status === 'fulfilled'
            ? (contributions.value?.total?.lastYear ?? null)
            : null,
      };

      setStats(next);
      setLoading(false);
      if (next.publicRepos !== null || next.contributionsLastYear !== null) {
        writeCache(next);
      }
    };

    load().catch(() => {
      if (!signal.aborted) setLoading(false);
    });

    return () => controller.abort();
  }, []);

  return { stats, loading };
};

